import { Component } from '@theme/component';

/** @extends {Component<{controls: HTMLElement, sort: HTMLSelectElement, status: HTMLElement, list: HTMLElement, more: HTMLButtonElement, empty: HTMLElement}>} */
class KindneoReviewWall extends Component {
  #filter = 'all';
  #shown = 6;
  /** @type {HTMLElement[]} */
  #items = [];
  /** @type {AbortController | undefined} */
  #controller;

  connectedCallback() {
    super.connectedCallback();
    this.#initialize();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#initialize();
  }

  disconnectedCallback() {
    this.#controller?.abort();
    super.disconnectedCallback();
  }

  #initialize() {
    this.#controller?.abort();
    this.#controller = new AbortController();
    this.#items = Array.from(this.querySelectorAll('[data-review]')).filter((item) => item instanceof HTMLElement);
    this.#filter = 'all';
    this.#shown = 6;
    if (this.refs.controls) this.refs.controls.hidden = false;
    for (const button of this.querySelectorAll('button[data-filter]')) button.removeAttribute('disabled');
    // Selecting a hidden review in the theme editor must reveal that exact block.
    document.addEventListener('shopify:block:select', (event) => {
      if (!(event.target instanceof Element)) return;
      const item = event.target.closest('[data-review]') || event.target.querySelector('[data-review]');
      if (!(item instanceof HTMLElement) || !this.contains(item)) return;
      this.#filter = 'all';
      this.#shown = this.#items.length;
      this.#render();
    }, { signal: this.#controller.signal });
    this.#render();
  }

  /** @param {MouseEvent} event */
  filter(event) {
    if (!(event.target instanceof Element)) return;
    const button = event.target.closest('button[data-filter]');
    if (!(button instanceof HTMLButtonElement)) return;
    this.#filter = button.dataset.filter || 'all';
    this.#shown = 6;
    this.#render();
  }

  sort() {
    this.#shown = 6;
    this.#render();
  }

  more() {
    const visible = this.#items.filter((item) => !item.hidden);
    this.#shown += 6;
    this.#render();
    const revealed = Array.from(this.refs.list?.querySelectorAll('[data-review]') || []).find((item) => item instanceof HTMLElement && !item.hidden && !visible.includes(item));
    if (revealed instanceof HTMLElement) {
      revealed.tabIndex = -1;
      revealed.focus({ preventScroll: true });
    }
  }

  #render() {
    const { list, sort, status, more, empty } = this.refs;
    if (!(list instanceof HTMLElement)) return;
    const kept = this.#items.filter((item) => {
      if (this.#filter === 'all') return true;
      if (this.#filter === 'photos') return item.dataset.photo === 'true';
      if (this.#filter === 'verified') return item.dataset.verified === 'true';
      return item.dataset.rating === this.#filter;
    });
    kept.sort((a, b) => {
      const rating = Number(b.dataset.rating) - Number(a.dataset.rating);
      const date = Number(b.dataset.date) - Number(a.dataset.date);
      if (sort?.value === 'highest') return rating || date;
      if (sort?.value === 'lowest') return -rating || date;
      return date;
    });
    for (const item of this.#items) item.hidden = true;
    for (const [index, item] of kept.entries()) {
      item.hidden = index >= this.#shown;
      list.append(item);
    }
    for (const button of this.querySelectorAll('button[data-filter]')) {
      button.setAttribute('aria-pressed', String(button.getAttribute('data-filter') === this.#filter));
    }
    if (status) status.textContent = (this.dataset.countTemplate || '').replace('[shown]', String(Math.min(kept.length, this.#shown))).replace('[total]', String(kept.length));
    if (empty) empty.hidden = kept.length > 0;
    if (more) more.hidden = kept.length <= this.#shown;
  }
}

/** @extends {Component<{track: HTMLElement, previous: HTMLButtonElement, next: HTMLButtonElement}>} */
class KindneoCustomerPosts extends Component {
  /** @type {AbortController | undefined} */
  #controller;
  /** @type {ResizeObserver | undefined} */
  #observer;

  connectedCallback() {
    super.connectedCallback();
    this.#bind();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#bind();
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#observer?.disconnect();
    super.disconnectedCallback();
  }

  #bind() {
    this.#controller?.abort();
    this.#observer?.disconnect();
    this.#controller = new AbortController();
    const track = this.refs.track;
    if (!(track instanceof HTMLElement)) return;
    track.addEventListener('scroll', () => this.#update(), { passive: true, signal: this.#controller.signal });
    this.#observer = new ResizeObserver(() => this.#update());
    this.#observer.observe(track);
    document.addEventListener('shopify:block:select', (event) => {
      if (!(event.target instanceof Element)) return;
      const card = event.target.closest('.kn-posts__card');
      if (card && this.contains(card)) card.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'instant' });
    }, { signal: this.#controller.signal });
    this.#update();
  }

  #update() {
    const { track, previous, next } = this.refs;
    if (!(track instanceof HTMLElement)) return;
    const offset = Math.abs(track.scrollLeft);
    if (previous) previous.disabled = offset <= 2;
    if (next) next.disabled = offset >= track.scrollWidth - track.clientWidth - 2;
  }

  /** @param {number} direction */
  #move(direction) {
    const track = this.refs.track;
    if (!(track instanceof HTMLElement)) return;
    const card = track.querySelector('.kn-posts__card');
    if (!(card instanceof HTMLElement)) return;
    const rtl = getComputedStyle(track).direction === 'rtl';
    const step = card.getBoundingClientRect().width + parseFloat(getComputedStyle(track).columnGap);
    track.scrollBy({ left: step * direction * (rtl ? -1 : 1), behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
  }

  previous() { this.#move(-1); }
  next() { this.#move(1); }
}

if (!customElements.get('kn-review-wall')) customElements.define('kn-review-wall', KindneoReviewWall);
if (!customElements.get('kn-customer-posts')) customElements.define('kn-customer-posts', KindneoCustomerPosts);
